import { useSelector } from "react-redux";
import {
  StyledQuantityToggle,
  StyledQuantityToggleButton,
  StyledMinQuantityInfo,
} from "./styles";
import { safelyGetString } from "@/utils/stringHelpers";
import { type RootState } from "@/types/app";

interface Props {
  quantity: number;
  maxQuantity: number;
  minQuantity?: number;
  quantityIncrement?: number;
  onToggle: (quantity: number) => void;
}

const QuantityToggle = ({
  onToggle,
  quantity,
  maxQuantity,
  minQuantity = 1,
  quantityIncrement = 1,
}: Props) => (
  <StyledQuantityToggle>
    <span>Qty.</span>
    <StyledQuantityToggleButton
      inactive={quantity <= minQuantity}
      aria-label={`Decrease the quantity by ${quantityIncrement}`}
      onClick={() => {
        // Ensure keyboard manipulation can't exceed min
        const newQuantity = quantity - quantityIncrement;
        if (newQuantity < minQuantity) {
          return;
        }

        onToggle(newQuantity);
      }}
    >
      &ndash;
    </StyledQuantityToggleButton>
    <span>{quantity}</span>
    <StyledQuantityToggleButton
      inactive={quantity >= maxQuantity}
      aria-label={`Increase the quantity by ${quantityIncrement}`}
      onClick={() => {
        // Ensure keyboard manipulation can't exceed max
        const newQuantity = quantity + quantityIncrement;
        if (newQuantity > maxQuantity) {
          return;
        }

        onToggle(newQuantity);
      }}
    >
      +
    </StyledQuantityToggleButton>
    {minQuantity > 1 && (
      <StyledMinQuantityInfo
        title={`Minimum quantity: ${minQuantity}`}
        aria-label={`Minimum quantity required: ${minQuantity}`}
      >
        <span>
          {safelyGetString(
            useSelector((state: RootState) => state.app.cmsConfig.strings),
            "product_details.min_quantity_info",
            { minQuantity }
          )}
        </span>
      </StyledMinQuantityInfo>
    )}
  </StyledQuantityToggle>
);

export default QuantityToggle;
